import * as Sentry from "@sentry/react";
import { Bug } from "@styled-icons/boxicons-regular/Bug";
import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import DataStore from "../components/DataStore";
import DataStoreContext from "../components/DataStore/DataStoreContext";
import AppMovedBanner from "../components/AppMovedBanner";
import Firebase, { FirebaseContext } from "../components/Firebase";
import Loading from "../components/Loading/index";
import LogRoutes from "../components/LogRoutes";
import Navigation from "../components/Navigation";
import { withAuthentication } from "../components/Session";
import * as ROUTES from "../constants/routes";
import Organization from "../screens/Organization";
import { AppContainer, Container, NotFoundContainer } from "./style";

const ScenarioPage = lazy(() => import("../screens/Scenario"));
const ScenariosList = lazy(() => import("../screens/ScenariosList"));
const SessionPage = lazy(() => import("../screens/Session"));
// const SharedSessionPage = lazy(() => import("../screens/Session"));
const SessionsList = lazy(() => import("../screens/SessionsList"));
const HomePage = lazy(() => import("../screens/Home"));
const Admin = lazy(() => import("../screens/Admin"));
const ScenarioEditor = lazy(() => import("../screens/ScenarioEditor"));
const ScenarioWizard = lazy(() => import("../screens/ScenarioEditor/WizardSlides/ScenarioWizard"));
const Help = lazy(() => import("../screens/Help"));
const UserProfile = lazy(() => import("../screens/UserProfile"));
const Voices = lazy(() => import("../screens/Voices"));
const SignInPage = lazy(() => import("../screens/SignIn"));
const PasswordForgetPage = lazy(() => import("../screens/PasswordForget"));

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const App = ({ authUser }) => {
  return (
    <LogRoutes>
      <AppContainer>
        <DataStoreContext>
          <Navigation />
          <Container>
            <AppMovedBanner />
            <Sentry.ErrorBoundary fallback={ErrorComponent}>
              {authUser && <DataStore />}
              <Suspense fallback={<Loading containerHeight="100dvh" />}>
                <SentryRoutes>
                  <Route path={`${ROUTES.PLAY_PAGE}/:id`} element={<ScenarioPage authUser={authUser} />} />
                  <Route path={ROUTES.HELP} element={<Help />} />
                  <Route path={`${ROUTES.LEGACY_SCENARIO_VIEW}/:id`} element={<LegacyScenarioRedirect />} />
                  <Route path={ROUTES.SCENARIOS} element={<ScenariosList />} />
                  <Route path={`${ROUTES.SESSIONS}/:id`} element={<SessionPage />} />
                  <Route path={ROUTES.SESSIONS} element={<SessionsList />} />
                  <Route path={ROUTES.HOME} element={<HomePage />} />
                  <Route path={ROUTES.ADMIN} element={<Admin />} />
                  <Route path={ROUTES.SCENARIO_EDITOR} element={<ScenarioEditor />} />
                  <Route path={`${ROUTES.SCENARIO_EDITOR}/:id`} element={<ScenarioEditor />} />
                  <Route path={ROUTES.SCENARIO_WIZARD} element={<ScenarioWizard />} />
                  <Route path={ROUTES.ORG_SETTINGS} element={<Organization />} />
                  <Route path={ROUTES.ORG_VOICES} element={<Voices />} />
                  <Route path={ROUTES.USER_PROFILE} element={<UserProfile />} />
                  <Route path="/" element={<LandingRedirect />} />
                  <Route
                    path={ROUTES.SIGN_IN}
                    element={<SignInPage key={"sign-in"} authUser={authUser} signUp={false} />}
                  />
                  <Route
                    path={ROUTES.SIGN_UP}
                    element={<SignInPage key={"sign-up"} authUser={authUser} signUp={true} />}
                  />
                  <Route path={ROUTES.PASSWORD_FORGET} element={<PasswordForgetPage />} />
                  <Route path="*" element={<NotFoundComponent />} />
                </SentryRoutes>
              </Suspense>
            </Sentry.ErrorBoundary>
          </Container>
        </DataStoreContext>
      </AppContainer>
    </LogRoutes>
  );
};

const AuthenticatedApp = withAuthentication(App);

// Did this just to ensure router is known by withAuthentication. Would prefer a
// cleaner and more consistent way of handling all these Higher Order Components
const RoutedApp = () => {
  return (
    <>
      <FirebaseContext.Provider value={new Firebase()}>
        <Router>
          <AuthenticatedApp />
        </Router>
      </FirebaseContext.Provider>
    </>
  );
};

export default RoutedApp;

const LandingRedirect = () => {
  const location = useLocation();
  const nextPath = location.search !== "" ? `${ROUTES.HOME}${location.search}` : ROUTES.HOME;

  return <Navigate replace to={nextPath} />;
};

const LegacyScenarioRedirect = () => {
  const { id } = useParams();
  const location = useLocation();
  const scenarioId = encodeURIComponent(id || "");
  return <Navigate replace to={`${ROUTES.PLAY_PAGE}/${scenarioId}${location.search || ""}`} />;
};

const NotFoundComponent = () => {
  return (
    <NotFoundContainer>
      <Bug />
      <h1 style={{ padding: "0" }}>404</h1>
      <h2>Page not found</h2>

      <h5>
        The page you are looking for doesn't exist.
        <br /> Go back or select a new direction from the navigation menu.
      </h5>
    </NotFoundContainer>
  );
};

const ErrorComponent = ({ error }) => {
  return (
    <NotFoundContainer>
      <Bug />
      <h2>An error happened</h2>

      <h5>
        {error.toString()}
        <br />
        The error has been reported, click to add more details.
        <br />
        Reload the page or try another page to continue.
      </h5>
    </NotFoundContainer>
  );
};
